/**
 * Analysis Guidelines - Toggle-gated XML instructions for LLM
 * Provides detailed HOW-TO guidance for each enabled toggle field.
 * Complements output-format.ts (WHAT to return) with analysis methodology.
 *
 * Category-aware: greeting, closing, and needs guidelines adapt to callCategory + channel.
 */

import type { PromptToggles } from '@aica/core';
import { OBJECTION_CODE_DEFINITIONS, OBJECTION_CODES } from '@aica/core/voc';
import type { CallCategory, Channel } from '../call-categories';
import {
  CRM_CONTEXT_UNAVAILABLE,
  CRM_NOT_CONNECTED,
  CRM_NO_LINKED_ENTITY,
} from '../crm-context-sentinels';
import { isSalesCategory } from '../call-categories';
import { escapeXmlText } from './escape';
import { AGENT_ATTRIBUTION_RULE, PROFANITY_NOT_CHECKED_RULE } from './common-sections';

type BaseCategory = 'sales-call' | 'support-call' | 'sales-chat' | 'complaint';

interface AnalysisGuidelinesOptions {
  toggles: PromptToggles;
  category: BaseCategory;
  callCategory?: CallCategory;
  channel?: Channel;
  targetCrmFields?: Array<{
    fieldId: string;
    title: string;
    type: string;
    enumValues?: Array<{ id: string; value: string }>;
  }>;
}

/**
 * Build toggle-gated analysis guidelines XML section.
 * Only includes guidelines for enabled toggles.
 */
export function buildAnalysisGuidelines(options: AnalysisGuidelinesOptions): string {
  const { toggles, category, targetCrmFields } = options;
  const callCat = options.callCategory;
  const channel: Channel =
    options.channel || (category === 'sales-chat' ? 'chat' : 'call');
  const isSales = callCat
    ? isSalesCategory(callCat)
    : category === 'sales-call' || category === 'sales-chat';
  const guidelines: string[] = [];
  // === A. Summary Metrics ===
  if (toggles.qualityScore) {
    guidelines.push(`
    <guideline field="quality_score">
      Calculate using weights from evaluation_framework above.
      Score each component independently, then compute weighted sum.
      Range: 0-100. Round to nearest integer.
      If insufficient evidence for a component, use conservative estimate.
      Consistency: the final score MUST be coherent with the sub-scores you assigned
      (greeting, clarity, politeness, objection/closing, etc.) — do not output a quality_score
      that contradicts strong or weak sub-scores. Reward conversation structure, resolution and
      clear next steps; do NOT reward mere length or verbosity. A long, rambling call with no
      structure or outcome should score LOWER than a short, well-handled one.
    </guideline>`);
  }

  if (toggles.saleProbability && isSales) {
    guidelines.push(`
    <guideline field="sale_probability">
      Anchor by the strongest commitment evidence in the call, then fine-tune:
        0.90-1.00: order placed / invoice issued / contract signed / explicit "оформляю, виставляйте рахунок".
        0.70-0.85: verbal agreement to buy + a concrete next step (date, prepayment, paperwork).
        0.45-0.65: genuine interest, some BANT signals, but no decision yet.
        0.30-0.45: active comparison-shopping or price-asking with no commitment.
        0.05-0.20: "just looking", no budget/authority, declined, or strong competitor preference.
      Move DECISIVELY with evidence — do NOT default-cluster around 0.5-0.65.
      A completed order must score high even if the conversation was short; a long, friendly,
      but non-committal call must score low. Range: 0.0-1.0. Precision: 0.05 increments.
    </guideline>`);
  }

  if (toggles.attentionFlag) {
    guidelines.push(`
    <guideline field="attention_flag">
      Use the severity triggers defined in the attention_flags section. Set triggered=false
      ONLY when no trigger applies. Hard rules:
      - ${PROFANITY_NOT_CHECKED_RULE}
      - ${AGENT_ATTRIBUTION_RULE}
      - DO fire (triggered=true) on real escalations — these are the high-value cases and are
        commonly MISSED. Set HIGH when ANY of:
          * explicit churn/defection intent: "не хочу більше мати діло", "піду до конкурента",
            "відмовляюсь від співпраці", "більше у вас не куплю";
          * defection to a competitor: a customer of ours has chosen one or is moving to one.
            A prospect already using another vendor, or comparing offers, is not defection —
            report it in business_intelligence.competitor_mentions and sale_probability where
            those fields apply;
          * sentiment.delta &lt; -0.5 (sentiment crash).
        A high sale_probability is NOT a trigger: the flag ranks the need for human
        intervention, and a promising deal is already reported by sale_probability itself.
        Set CRITICAL on legal threats (суд, юрист, скарга в органи), threats of violence,
        discrimination, data breach, or regulatory violation — these triggers apply to
        agent↔customer interactions (misconduct toward or risk to a customer), not to what
        private parties say to each other on a personal call.
      - If any key_moment.importance is "critical" (violence, legal action, discrimination,
        a data breach or a regulatory violation),
        triggered MUST be true with a matching severity. Never output triggered=false while a
        critical key_moment exists.
      - NEGATIVE examples (triggered=false): a personal/non-customer call where both sides
        swear in a friendly mutual register — no agent misconduct and no customer at risk;
        a customer who briefly vents about money or prices, then calms down and ends the
        call cooperatively — frustration that self-de-escalates is not an escalation;
        offensive or discriminatory slang between private parties on a personal/non-customer
        call — do not mark such a moment "critical",
        but with no customer at risk and no agent misconduct there is nothing to escalate.
      - Set triggered=true only for a trigger of the attention_flags section (any
        severity, CRITICAL to LOW) or of the client's attention_flag_overrides, and
        name that trigger in the "reason". If none applies, triggered=false.${
          toggles.keyMoments
            ? `
      - When key_moments is part of this request's output and the trigger is something
        said in the conversation, list that utterance among the key moments and set
        supports_attention_flag=true on it — the flag's evidence is read from that
        moment's quote. When key_moments is disabled for this request, decide the flag
        exactly as above: a missing key moment is never a reason to withhold a flag.`
            : ''
        }
      - CONSISTENCY: triggered and reason must agree. If the reason you write concludes no
        real escalation or misconduct occurred ("клієнт заспокоївся", "розмова дружня"),
        triggered MUST be false — never emit triggered=true with a reason that talks the
        flag back down.
      - "reason" is customer-facing: a clean sentence, NO internal notes/placeholders
        (e.g. "[needs_manual_review]") and no framework name (say "qualification", not BANT).
    </guideline>`);
  }

  // === B. Content Analysis ===
  if (toggles.topic) {
    guidelines.push(`
    <guideline field="topic">
      Identify the primary business topic of the conversation.
      Use specific, business-relevant categories (e.g., "CRM software evaluation",
      "billing dispute resolution"), NOT generic labels ("phone call", "customer inquiry").
      Max 10 words.
    </guideline>`);
  }

  if (toggles.keywords) {
    guidelines.push(`
    <guideline field="keywords">
      Extract 5-10 key terms from the conversation.
      Priority order: (1) product/service names, (2) business terms, (3) pain points,
      (4) competitor mentions, (5) technical terms.
      Exclude common words (the, is, and). Use lowercase.
    </guideline>`);
  }

  if (toggles.keyMoments) {
    // key_moments is the only output enum that spells the non-agent party
    // "customer" — stop_words, profanity and the pipeline's own SPEAKER_ROLES
    // all say "client", which is what authoritative_speaker_roles renders.
    // Unifying the enum would change a value already stored, read by the
    // analyze safety gates and printed by the UI, so the prompt carries the
    // synonym instead: without it "never guess" leaves a client line with no
    // authoritative value in reach and it falls into "unknown".
    //
    // supports_attention_flag (#1993) is in the schema only when the PROFILE has
    // both attentionFlag and keyMoments on, but `toggles` here is not the
    // profile: system-builder.ts passes the widest set the channel allows, so on
    // a call this text and the attention_flag bullet about it are always
    // printed. Both therefore condition themselves on the request ("when …
    // enabled for this request") instead of stating the field unconditionally.
    guidelines.push(`
    <guideline field="key_moments">
      Identify 3-7 most significant moments in the conversation.
      Each moment requires: timestamp (MM:SS or null), label (short description),
      quote (exact words), importance (critical/high/medium/low), speaker (agent/customer/unknown)${
        toggles.attentionFlag
          ? ', supports_attention_flag (true/false; only when attention_flag is enabled for this request)'
          : ''
      }.
      Use "critical" for threats of violence, legal action (суд, юрист, скарга в органи),
      discrimination, a data breach, or a regulatory violation — and only there: the
      system floors attention_flag at CRITICAL on such a moment, so "critical" as mere
      emphasis raises a false escalation, while withholding it hides a real one.
      Churn/defection, competitor switching and a sentiment crash are "high", not
      "critical": attention_flags lists them under HIGH and already requires
      triggered=true there.
      Prioritize: (1) decision points, (2) objections, (3) commitments,
      (4) emotional shifts, (5) key information revealed.
      A timestamp is only a hint for the deterministic time-aligner. Never estimate from
      position: use null when exact timing is unavailable. Attribute speaker according to
      authoritative_speaker_roles; use unknown when the role is ambiguous, never guess.
      That section spells the non-agent party role="client"; this field's enum spells the
      same party "customer" — they are one role, so a client line is speaker="customer"
      here, not "unknown".
      The label MUST describe the quote it carries. If a speaker contradicts himself
      across the call, pick the quote that supports your label — do not label a moment
      "low signal detected" and attach the line where he said the signal was elevated.${
        toggles.attentionFlag
          ? `
      supports_attention_flag exists only when attention_flag is enabled for this request;
      when it is disabled the schema has no such field — do not emit it. Where it exists, it
      is true on exactly ONE moment: the one whose quote proves the trigger named in
      attention_flag.reason. It is false on every other moment, and on ALL moments when
      attention_flag.triggered=false or when the flag does not rest on a single utterance
      (3+ unhandled objections, a sentiment crash, a low score).`
          : ''
      }
    </guideline>`);
  }

  if (toggles.summary) {
    guidelines.push(`
    <guideline field="summary">
      Write 2-3 sentences covering: (1) main topic/purpose, (2) key outcome,
      (3) next steps or resolution. Be specific with numbers, products, amounts and dates.
      Avoid vague language. Ground STRICTLY in the transcript: never name a CRM, platform,
      system or product that was not explicitly spoken (e.g. do NOT write "Bitrix24",
      "crm.technotorg.com" or any vendor unless the speakers actually said it).
      PRIVACY: do NOT include full personal names, phone numbers,
      or emails of private individuals — refer to people by role (client/agent) or first
      name only. (Company/brand names actually mentioned are fine.)
    </guideline>`);
  }

  if (toggles.structuredSummary) {
    guidelines.push(`
    <guideline field="structured_summary">
      situation: 1-2 sentences describing context and customer state.
      actions: Array of 3-5 specific actions taken during conversation.
      outcome: 1-2 sentences describing result and any commitments made.
    </guideline>`);
  }

  // === C. Speech Analysis ===
  //
  // Счёт паразитов ведёт код, а не модель (#819): её число заменяется
  // детерминированным счётом звуков колебания в сегментах агента
  // (apps/workflows-api/src/workers/analyze/filler-words.ts). Модель считала
  // недостоверно — ноль при двадцати «е-е-е», `4.02026` в счётчике — и по
  // своему списку слов, который с показанным пользователю определением не
  // совпадает. Инструкция остаётся, потому что поле обязательно в строгой
  // схеме, но работу с модели снимает.
  if (toggles.fillerWords) {
    guidelines.push(`
    <guideline field="filler_words">
      Return {"count": 0, "examples": []}. This field is computed deterministically
      from the diarized recording after analysis; any value you return is replaced.
      Do not spend effort counting filler words.
    </guideline>`);
  }

  if (toggles.stopWords) {
    guidelines.push(`
    <guideline field="stop_words">
      Detect the AGENT hedging on their OWN commitment: price, deadline, or promise.
      Ukrainian/Russian: мабуть, можливо, я думаю, напевно, наче/ніби, спробую, я не впевнений,
      десь, якось, можеть бить/может быть, наверное, попробую, постараюсь.
      English: maybe, I think, possibly, I guess, try, hope, might, probably, perhaps, not sure.
      NOT hedging: retelling the customer's words (the hedge is theirs, not the agent's);
      imperative mood ("спробуйте" vs спробую) — instruction, not uncertainty; estimating the
      OTHER party's data ("я думаю, ви платите близько…") — honest guesswork, not a hedge on
      the agent's own word.
      Output "detected" as an array of objects, each: { token, speaker, quote }.
        - token: the hedging word/phrase, copied verbatim from the transcript.
        - speaker: who said it — set "agent" ONLY when the agent's resolved name labels the line.
          If it was the client or the speaker is uncertain, set "client"/"unknown" — not agent
          hedging.
        - quote: the verbatim sentence containing the token (proof it is really present).
      HARD RULES: every token MUST appear verbatim in its quote — do NOT invent hedges. Emit
      ONLY agent-spoken tokens, else []. Filler words (ну, типу, щас) and profanity NEVER go
      here (filler words belong in filler_words).
    </guideline>`);
  }

  if (toggles.operatorErrors) {
    guidelines.push(`
    <guideline field="operator_errors">
      Identify AGENT mistakes only. Classify by severity:
      Critical (score 9-10): wrong pricing, false promises, legal violations, data disclosure.
      Major (score 6-8): incorrect product info, missed escalation, wrong procedure.
      Minor (score 1-5): mispronunciation, minor process deviation, awkward phrasing.
      Each error needs: type, description with timestamp reference, kind, evidence_quote
      (verbatim; the system drops an error whose quote does not match).
      kind="said": the agent said/did something wrong; quote the AGENT's own line, never
      the customer's.
      kind="omitted": the agent failed to do what the call required; quote the line that
      called for it (usually the customer's request). Nothing asked for it = no error.
      A next step left hanging is an omitted error: the customer asked for a callback,
      for details to be sent (e.g. options in a messenger) or for something to be
      confirmed, and the agent never takes it on — ignores the request or leaves it open.
      Quote the request. An agent who agrees to do it is not an error, even without a
      time or channel; do not report a missing deadline or channel by itself.
      Agent profanity is a "said" error: type names it as profanity (e.g. "обсценна
      лексика"), evidence_quote is the agent's own obscene line, and only when that line is
      confidently the agent's — the system keeps it only if this call's separate check of the
      agent's language confirms it. Report rudeness/unprofessional language the same way, as
      kind="said" quoting the agent's own offending line (high confidence); otherwise emit
      none. Never blame the agent for the CUSTOMER's words or tone, and never
      fabricate process/qualification errors on a non-customer or personal call. Report only
      the agent's own conduct in a genuine customer interaction.
      NOT an error: on a short routing/dispatch call where the customer got exactly what they
      asked for (transfer made, callback arranged, pickup confirmed), the absence of discovery
      or qualification questions is not a "missed qualification" — do not demand sales
      technique on calls whose only goal was routing or logistics.
    </guideline>`);
  }

  // === D. Dialogue Technique (category-aware) ===
  if (toggles.greetingIntro) {
    if (channel === 'chat') {
      guidelines.push(`
    <guideline field="greeting_intro">
      Apply scoring rubric from scoring_rubrics section above.
      Focus on the first 1-2 messages of the conversation (not "first 30 seconds").
      Do NOT penalize for missing formal company introduction — chat greetings are shorter.
      Evaluate: personalization, response timeliness, tone, and context awareness.
      Award partial credit for incomplete but present elements.
    </guideline>`);
    } else {
      guidelines.push(`
    <guideline field="greeting_intro">
      Apply scoring rubric from scoring_rubrics section above.
      Focus on first 30 seconds of conversation.
      Award partial credit for incomplete but present elements.
      Check the call direction in the request and follow the rubric's direction_note: on an
      inbound call the purpose comes from the customer, and personalization leaves the score
      unless the customer's name is known. Outbound and unknown direction: all four criteria.
      A mutual informal register with an established contact (regular customer, first-name
      terms) is a stylistic choice, not a missing greeting — score the elements actually
      present instead of demanding a formal script the relationship makes unnatural.
      Provide specific feedback referencing what was done well or missed.
    </guideline>`);
    }
  }

  if (toggles.needsQualification) {
    // Category-aware guideline
    const frameworkName = callCat
      ? getFrameworkName(callCat)
      : isSales
        ? 'BANT'
        : 'framework';
    guidelines.push(`
    <guideline field="needs_qualification">
      Apply ${frameworkName} rubric from scoring_rubrics section above.
      Track which components were explored and to what depth.
      List specifically identified needs/items with evidence quotes.
      Score reflects qualification completeness, not conversation outcome.
      A component that could not sensibly apply to THIS call is not a failure:
      drop it from the denominator instead of scoring it 0, and never advise the
      agent to ask something the situation made meaningless. If no component
      applies, return score=null rather than 0 — 0 reads as "did it badly".
    </guideline>`);
  }

  if (toggles.objectionHandling) {
    // `type` stays a free human-readable label — the CRM report, the PDF and
    // the analysis panel print it verbatim. `code` is what the product groups
    // by: on prod 3 801 objections in 30 days carried 2 537 distinct labels,
    // and lower-casing merged none of them («ціна» / «price» / «бюджет» differ
    // in meaning of the record, not in spelling). OBJECTION_CODES (@aica/core)
    // is the single source of truth — it also drives the schema enum.
    //
    // The definitions live in @aica/core/voc: the label classifier that maps
    // historical free labels onto codes must describe every code with the SAME
    // words, or the two arms would disagree by construction.
    const objectionCodeLines = OBJECTION_CODES.map(
      (code) => `        ${code}: ${OBJECTION_CODE_DEFINITIONS[code]}`
    ).join('\n');
    guidelines.push(`
    <guideline field="objection_handling">
      Apply rubric from scoring_rubrics section above.
      Score EACH objection independently; the system computes the average.
      Return score (0-100) for every item; do not return an independent handled boolean.
      The system derives handled=true at score >= 60 and recomputes the average.
      An unaddressed objection scores 0. List each objection with handling quality.
      If no objections were raised, return an empty objections array — the system
      scores that 100. Never invent a placeholder entry to carry the score: a
      fabricated objection is stored, counted, printed in the CRM report and
      aggregated into the team objection-type breakdown.

      For every objection also return code: the ONE canonical reason, chosen by
      MEANING — the conversation language does not matter, codes stay in
      English, verbatim. type remains your short free label in the analysis
      language; code is how objections are counted across thousands of calls.
      <objection_codes>
${objectionCodeLines}
      </objection_codes>
      secondary_code exists for ONE objection that names two reasons in the
      same breath ("expensive, and you want full prepayment"): code is the
      reason the customer insists on more, secondary_code the other. It does
      NOT change how many objections you list: two objections raised at
      different moments stay two items, each scored on its own, exactly as
      before. Otherwise secondary_code is null. Never repeat code in it.
      Keep type as short as before — a label, not a sentence.
    </guideline>`);
  }

  if (toggles.closingTechniques) {
    // Category-aware closing guideline
    const closingContext = getClosingGuidelineContext(callCat);
    guidelines.push(`
    <guideline field="closing_techniques">
      Apply closing rubric from scoring_rubrics section above.
      ${closingContext}
      List specific techniques with timestamps and quotes.
      Multiple closing attempts: use the strongest one for scoring.
      NOT APPLICABLE CASE: if the conversation ended before any closing attempt was
      possible — stale or wrong contact, customer disengaged during qualification,
      call disconnected within the first ~30 seconds — set score to null (not 0) and
      leave techniques_used empty. This is different from attempting to close and
      failing (that scores 0 on the rubric above): null means closing was never
      applicable to this call.
    </guideline>`);
  }

  // === E. Emotions ===
  if (toggles.sentiment) {
    guidelines.push(`
    <guideline field="sentiment">
      Use NUMERIC values only (-1.0 to +1.0). NEVER use text like "positive"/"neutral".
      Calibration guide:
        +1.0 = extremely positive (grateful, excited, praising)
        +0.5 = positive (satisfied, interested, appreciative)
         0.0 = neutral (factual, professional, cooperative)
        -0.5 = negative (frustrated, skeptical, disappointed)
        -1.0 = extremely negative (angry, hostile, threatening)
      Score CLIENT sentiment only — do NOT score the agent/employee.
      Measure at 2 points: client_start, client_end.
      delta = client_end - client_start (positive = improvement).
      overall = client sentiment across the call, weighted toward end-of-call.
      Precision: 0.1 increments.
    </guideline>`);
  }

  if (toggles.politeness) {
    guidelines.push(`
    <guideline field="politeness">
      Score 0-100 for agent politeness.
      Positive indicators (+20 each): please/thank you, active listening acknowledgments,
        empathy statements, respectful tone, warm greeting/closing.
      Negative indicators (-20 each): interrupting customer, dismissive language,
        impatient tone, talking over customer.
      Severe negative (-30): rudeness, sarcasm, condescension.
      Mutual informal register (ти/ты, first names, colloquialisms) that the customer
      reciprocates in an established relationship is NOT impolite by itself — penalize
      genuine disrespect, not informality.
      Base score: 50. Add/subtract based on indicators. Clamp to 0-100.
      List specific polite phrases detected.
    </guideline>`);
  }

  if (toggles.interruptions) {
    guidelines.push(`
    <guideline field="interruptions">
      Count interruptions by each party (agent and customer).
      An interruption = speaking over the other party before they finish.
      Classify type: collaborative (finishing thought), disruptive (cutting off),
        clarifying (asking mid-sentence).
      Thresholds: 0-2 = normal, 3-5 = notable, 6+ = problematic.
      Report total count, by_agent count, by_customer count.
    </guideline>`);
  }

  if (toggles.clarity) {
    guidelines.push(`
    <guideline field="clarity">
      Score 0-100 based on 4 criteria (25 points each):
      (1) Jargon avoidance: explains technical terms, uses simple language.
      (2) Pace: appropriate speed, pauses for understanding.
      (3) Examples: uses concrete examples to illustrate points.
      (4) Verification: checks customer understanding ("Does that make sense?").
      Provide feedback explaining the score.
    </guideline>`);
  }

  // === F. Compliance ===
  if (toggles.crmCompleteness) {
    guidelines.push(`
    <guideline field="crm_completeness">
      Assess CRM data hygiene in TWO steps.

      STEP 1 — Applicability. Was capturing the customer's identifying details a
      task of THIS call? Judge by the CONTENT of &lt;crm_context&gt; — ignore its
      status attribute:
      - Content is exactly "${CRM_NOT_CONNECTED}" → this organisation has no CRM
        integration, so there is no CRM whose data hygiene could be measured.
        Set applicable=false, score=null, missing_fields=[]. STOP.
      - Content is exactly "${CRM_CONTEXT_UNAVAILABLE}" → the CRM lookup failed. Absence of
        evidence is not evidence: do not grade on a failed fetch. Set
        applicable=false, score=null, missing_fields=[]. STOP.
      - Content shows a linked DEAL / CONTACT / COMPANY ID → the customer is
        already on file, collecting their details is NOT the job. Set
        applicable=false, score=null, missing_fields=[].
        (Typical: outbound calls on an existing deal, repeat/known customers.)
      - Also set applicable=false when the call is clearly with an already-known
        party and no new identifying data is expected.
      If not applicable, STOP here — do not invent missing fields.

      STEP 2 — Score (only when applicable=true). This is the new/unidentified
      customer case: content is exactly "${CRM_NO_LINKED_ENTITY}" (a CRM exists but
      nothing is linked to this call) — or the agent is actively taking down
      details. Grade only these identifying fields:
        (1) customer / contact person name
        (2) contact info (phone or email)
        (3) company name — ONLY when the customer is a business. A private
            individual has no company, so on a consumer call the field is not
            applicable and MUST NOT count toward the denominator or appear in
            missing_fields. Judge from the call: a person arranging service for
            their own home is not a B2B customer.
      A field already shown in &lt;crm_context&gt; is NOT missing — never list it.
      Do NOT grade the reason/need for contact (covered by needs_qualification).
      Score = round(100 * collected_relevant_fields / applicable_relevant_fields).
      missing_fields: SHORT field names only in the output language
      (e.g. "company", "email") — never sentences or explanations.
    </guideline>`);
  }

  // === H. Business Intelligence ===
  if (toggles.businessIntelligence) {
    guidelines.push(`
    <guideline field="business_intelligence">
      Extract organically-mentioned business signals (not the customer's primary
      topic — those belong in summary/topic). All four sub-fields are REQUIRED
      arrays; return an empty array [] when no matching signals are present.

      <competitor_mentions>
        A competitor is a THIRD-PARTY vendor that sells a product/service SIMILAR to your
        company's offering, mentioned as an alternative/incumbent. Record name verbatim,
        short context (&lt;= 20 words), and sentiment (positive/neutral/negative).
        Do NOT classify as a competitor: your own company's brand or product lines; the
        customer's OWN equipment/assets or their own company; banks, lenders, insurers,
        logistics/delivery providers, payment processors; or a prospective end-customer.
        When in doubt whether something is truly a competing vendor, OMIT it.
      </competitor_mentions>

      <product_feedback>
        Feedback about YOUR company's own products/services ONLY — not the customer's own
        equipment/assets and not a third party's product. Include the specific product or
        model name when stated. Severity scale:
          praise: positive feedback, testimonial
          minor_issue: small inconvenience, workaround exists
          major_issue: blocks customer's work, needs fix
          incident: outage, defect, recall, safety/financial impact
      </product_feedback>

      <market_signals>
        EXTERNAL market intelligence only. Types:
          pricing: price sensitivity, willingness-to-pay, budget shifts
          supply: market-level shortages, lead times
          regulation: new laws, permits, audits
          trend: emerging patterns, category shifts, seasonality
          demand: volume signals, bulk/repeat intent, customer segments
        Do NOT log routine internal/operational steps as signals (e.g. "handover on Monday",
        "send the documents", "mechanic will come", "issue the invoice") — these are call
        logistics, not market signals. Return [] when there is no genuine external signal.
        Keep each signal to one short phrase.
      </market_signals>

      <compliance_flags>
        Any mention that should be reviewed by legal/finance/compliance:
        alternate-entity invoicing, cash-off-books requests, under-the-table
        arrangements, missing permits, data-handling concerns, unofficial
        discounts, kickbacks, bribery. Record short label + verbatim context.
        DO NOT speculate — flag only explicit mentions.
      </compliance_flags>

      If a mention is ambiguous (could be competitor OR company's own product),
      prefer the most specific interpretation and include in one category only.
    </guideline>`);
  }

  // === G. Recommendations ===
  if (toggles.strengthsImprovements) {
    guidelines.push(`
    <guideline field="strengths_improvements">
      Provide 3-5 strengths and 3-5 improvements.
      Each strength: behavior (what agent did well) + impact (business result).
      Each improvement: behavior (what to change) + suggestion (specific action).
      Prioritize by business impact. Be specific, not generic.
      Reference transcript evidence for each item.
    </guideline>`);
  }

  if (toggles.extractedCrmFields && targetCrmFields && targetCrmFields.length > 0) {
    const fieldLines = targetCrmFields
      .map((f) => {
        const opts = f.enumValues?.length
          ? ` Allowed options: ${f.enumValues.map((e) => escapeXmlText(e.value)).join(', ')}.`
          : '';
        return `- ${escapeXmlText(f.fieldId)} ("${escapeXmlText(f.title)}", type=${escapeXmlText(f.type)}).${opts}`;
      })
      .join('\n      ');
    guidelines.push(`
    <guideline field="extracted_crm_fields">
      Extract values ONLY for these CRM fields, ONLY when the transcript gives clear evidence:
      ${fieldLines}
      Format each value to its type: enum -> exact option label; date/datetime -> ISO 8601;
      number/money -> digits only. Include a short evidence_quote and confidence (0..1).
      Omit a field entirely if there is no clear evidence — do not guess.
    </guideline>`);
  }

  if (toggles.agreements) {
    guidelines.push(`
    <guideline field="agreements">
      Extract concrete follow-up commitments (callback, send proposal/email, meeting, internal task).
      For each: type (call|meeting|email|task), a short subject, a description, the spoken timing
      phrase (due_hint), and due_at.
      ALWAYS resolve due_at to a concrete ISO 8601 date (YYYY-MM-DD, or a full datetime) anchored to
      the call time whenever ANY timing is mentioned — INCLUDING vague periods: map a period to its
      boundary date (e.g. "до конца недели"/"end of week" -> that Friday; "до конца лета"/"end of
      summer" -> Aug 31; "в начале следующего месяца"/"early next month" -> 1st of next month;
      "через пару дней"/"in a couple days" -> call date + 2 days). Leave due_at empty ONLY when no
      timing is mentioned at all. Keep the original spoken phrase in due_hint regardless.
      Add confidence (0..1), an evidence_quote, and evidence_timestamp (MM:SS or HH:MM:SS)
      pointing to that exact quote in the transcript. Do NOT invent commitments.
    </guideline>`);
  }

  if (guidelines.length === 0) {
    return '';
  }

  return `
  <analysis_guidelines>${guidelines.join('')}
  </analysis_guidelines>`;
}

/**
 * Get framework name for needs qualification guideline
 */
function getFrameworkName(category: CallCategory): string {
  const names: Record<CallCategory, string> = {
    purchase: 'BANT',
    product_inquiry: 'Problem-Solution Fit',
    scheduling: 'Meeting Prep',
    support: 'Troubleshooting',
    billing: 'Issue ID',
    onboarding: 'Training Needs',
    complaint: 'Complaint Assessment',
    cancellation: 'Churn Analysis',
    follow_up: 'Context Recall',
    other: 'General Inquiry',
  };
  return names[category];
}

/**
 * Get closing guideline context based on category + channel
 */
function getClosingGuidelineContext(category: CallCategory | undefined): string {
  if (!category) {
    return 'Match the closing technique used to the rubric options.';
  }

  switch (category) {
    case 'purchase':
    case 'product_inquiry':
    case 'scheduling':
      return 'Match the closing/sales technique used to the rubric options. Focus on conversion actions and clear next steps.';

    case 'support':
    case 'billing':
      return 'Evaluate resolution quality: was the issue resolved? Was confirmation obtained? Was follow-up scheduled if needed?';

    case 'complaint':
      return 'Evaluate de-escalation outcome: was the complaint resolved? Was the customer retained? Was the situation made better or worse?';

    case 'cancellation':
      return 'Evaluate retention effort: was the cancellation reason explored? Was a retention offer made? Was the exit handled gracefully?';

    default:
      return 'Match the closing technique used to the rubric options.';
  }
}
